import { ReactElement, useCallback, useMemo, memo } from 'react';
import Box from '@mui/material/Box';
import List from '@mui/material/List';
import Typography from '@mui/material/Typography';
import { FormattedMessage } from 'intl';
import { NotificationTranslationKeys } from 'intl/keys';

import { DocumentationBrowserSkeleton } from './DocumentationBrowserSkeleton';
import { isArchived } from 'components/documentation/DocumentationDataGrid/model';
import { DirectoryModel } from 'shared/domain/directory/directoryModel';
import { DocumentationModel } from 'shared/domain/documentation/documentationModel';
import {
  DirectoryRow,
  DocumentationRow,
} from './DocumentationDirectoryListRow';
import { getDirectoryRowId, getDocumentationRowId } from './rowId';
import {
  listContainerSx,
  listEmptyBoxSx,
  listEmptyMessageSx,
  listLoadingBoxSx,
} from './styles';

interface DocumentationDirectoryListProps {
  directories: DirectoryModel[];
  emptyDirectoryIds?: Set<string>;
  documentations: DocumentationModel[];
  loading: boolean;
  loadError: string | null;
  selectedDirectoryId: string | null;
  onRowClick: (id: string) => void;
  onRowDoubleClick: (id: string) => void;
  selectedDocumentationLocalIds?: string[];
  onDocumentationRowClick?: (id: string) => void;
}

function DocumentationDirectoryListInner({
  directories,
  emptyDirectoryIds,
  documentations,
  loading,
  loadError,
  selectedDirectoryId,
  onRowClick,
  onRowDoubleClick,
  selectedDocumentationLocalIds = [],
  onDocumentationRowClick,
}: DocumentationDirectoryListProps): ReactElement {
  const filteredDirectories = useMemo(
    () => directories.filter((d) => !isArchived(d)),
    [directories]
  );

  const filteredDocumentations = useMemo(
    () => documentations.filter((d) => !isArchived(d)),
    [documentations]
  );

  const handleRowClick = useCallback(
    (id: string) => {
      onRowClick(id);
    },
    [onRowClick]
  );

  const handleRowDoubleClick = useCallback(
    (id: string) => {
      onRowDoubleClick(id);
    },
    [onRowDoubleClick]
  );

  const selectedDocumentationLocalIdsSet = useMemo(
    () => new Set(selectedDocumentationLocalIds),
    [selectedDocumentationLocalIds]
  );

  if (loading) {
    return (
      <Box sx={listLoadingBoxSx}>
        <DocumentationBrowserSkeleton />
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box sx={listEmptyBoxSx}>
        <Typography variant='body2' sx={listEmptyMessageSx}>
          <FormattedMessage id={NotificationTranslationKeys.somethingWentWrong} />
        </Typography>
      </Box>
    );
  }

  return (
    <List disablePadding sx={listContainerSx}>
      {filteredDirectories.map((directory) => {
        const id = getDirectoryRowId(directory);
        return (
          <DirectoryRow
            key={id}
            directory={directory}
            selected={Boolean(directory._id) && selectedDirectoryId === directory._id}
            isEmptyDirectory={
              typeof directory._id === 'string' &&
              Boolean(emptyDirectoryIds?.has(directory._id))
            }
            onRowClick={handleRowClick}
            onRowDoubleClick={handleRowDoubleClick}
          />
        );
      })}
      {filteredDocumentations.map((documentation) => (
        <DocumentationRow
          key={getDocumentationRowId(documentation)}
          documentation={documentation}
          selected={selectedDocumentationLocalIdsSet.has(
            String(documentation.localId)
          )}
          onRowClick={onDocumentationRowClick}
        />
      ))}
    </List>
  );
}

export const DocumentationDirectoryList = memo(
  DocumentationDirectoryListInner
);
